import { gameRgba } from "@/lib/game-palette"
import { cssEase, GAME_DURATION, GAME_EASE, toMs } from "@/lib/motion"
import { WHEEL_PALETTE } from "./prize-wheel-palette"

/** Radius of the scratching coin, in CSS pixels. */
export const SCRATCH_RADIUS = 18

/** Columns of the grid that measures how much foil is gone. */
export const SCRATCH_COLS = 12

/** Rows of the grid that measures how much foil is gone. */
export const SCRATCH_ROWS = 8

/** Share of the foil that, once scratched, reveals the whole ticket. */
export const SCRATCH_REVEAL_SHARE = 0.5

const { foil, gold } = WHEEL_PALETTE
const NOISE_SEED = 7

/** The gold coin (28 px) as a CSS image. */
export const COIN_IMAGE = `url("data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28"><circle cx="14" cy="14" r="12.5" fill="${gold.deep}"/><circle cx="14" cy="14" r="10.5" fill="${gold.mid}"/><circle cx="14" cy="14" r="7.5" fill="none" stroke="${gold.deep}" stroke-width="1.2"/><circle cx="11" cy="10.5" r="2.4" fill="${gold.light}"/></svg>`,
)}")`

/** A coin-shaped cursor (hotspot in its center) for scratching with a mouse. */
export const COIN_CURSOR = `${COIN_IMAGE} 14 14, pointer`

function random(seed: number): () => number {
  let state = seed
  return () => {
    state = (state * 16807) % 2147483647
    return state / 2147483647
  }
}

function paintCoin(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, font: string) {
  const face = ctx.createLinearGradient(x - radius, y - radius, x + radius, y + radius)
  face.addColorStop(0, gold.light)
  face.addColorStop(0.55, gold.mid)
  face.addColorStop(1, gold.deep)
  ctx.save()
  ctx.shadowColor = gameRgba("ink", 0.35)
  ctx.shadowBlur = 4
  ctx.shadowOffsetY = 2
  ctx.beginPath()
  ctx.arc(x, y, radius, 0, Math.PI * 2)
  ctx.fillStyle = gold.deep
  ctx.fill()
  ctx.restore()
  ctx.beginPath()
  ctx.arc(x, y, radius * 0.86, 0, Math.PI * 2)
  ctx.fillStyle = face
  ctx.fill()
  ctx.beginPath()
  ctx.arc(x, y, radius * 0.62, 0, Math.PI * 2)
  ctx.strokeStyle = gold.deep
  ctx.lineWidth = Math.max(1, radius * 0.08)
  ctx.stroke()
  ctx.fillStyle = gold.deep
  ctx.textAlign = "center"
  ctx.textBaseline = "middle"
  ctx.font = `700 ${Math.round(radius * 0.8)}px ${font}`
  ctx.fillText("$", x, y + radius * 0.04)
}

/** Paints the silver scratch foil in the display `font`: brushed metal gradient, fine noise, a diagonal sheen, a gold coin and an embossed "RASPE AQUI". */
export function paintFoil(ctx: CanvasRenderingContext2D, width: number, height: number, font: string) {
  ctx.globalCompositeOperation = "source-over"
  const metal = ctx.createLinearGradient(0, 0, width, height)
  metal.addColorStop(0, foil.mid)
  metal.addColorStop(0.28, foil.light)
  metal.addColorStop(0.52, foil.deep)
  metal.addColorStop(0.76, foil.light)
  metal.addColorStop(1, foil.mid)
  ctx.fillStyle = metal
  ctx.fillRect(0, 0, width, height)

  const next = random(NOISE_SEED)
  const dots = Math.round((width * height) / 14)
  for (let dot = 0; dot < dots; dot += 1) {
    const light = next() > 0.5
    ctx.fillStyle = light ? gameRgba("bulb", 0.12 + next() * 0.2) : gameRgba("ink", 0.04 + next() * 0.08)
    ctx.fillRect(next() * width, next() * height, 1, 1)
  }

  ctx.strokeStyle = gameRgba("bulb", 0.08)
  ctx.lineWidth = 1
  for (let x = -height; x < width; x += 5) {
    ctx.beginPath()
    ctx.moveTo(x, height)
    ctx.lineTo(x + height, 0)
    ctx.stroke()
  }

  const sheen = ctx.createLinearGradient(0, height, width, 0)
  sheen.addColorStop(0.32, gameRgba("bulb", 0))
  sheen.addColorStop(0.48, gameRgba("bulb", 0.55))
  sheen.addColorStop(0.53, gameRgba("bulb", 0.15))
  sheen.addColorStop(0.66, gameRgba("bulb", 0))
  ctx.fillStyle = sheen
  ctx.fillRect(0, 0, width, height)

  paintCoin(ctx, width / 2, height * 0.31, Math.min(width, height) * 0.14, font)

  const size = Math.round(Math.min(width * 0.095, height * 0.16))
  const textY = height * 0.6
  ctx.textAlign = "center"
  ctx.textBaseline = "middle"
  ctx.font = `700 ${size}px ${font}`
  ctx.fillStyle = gameRgba("bulb", 0.9)
  ctx.fillText("RASPE AQUI", width / 2 - 1, textY - 1)
  ctx.fillStyle = gameRgba("ink", 0.45)
  ctx.fillText("RASPE AQUI", width / 2 + 1, textY + 1)
  ctx.fillStyle = foil.mid
  ctx.fillText("RASPE AQUI", width / 2, textY)
}

/** Scrapes a coin-wide stroke of foil from `from` to `to`. */
export function scrapeFoil(ctx: CanvasRenderingContext2D, from: { x: number; y: number }, to: { x: number; y: number }) {
  ctx.globalCompositeOperation = "destination-out"
  ctx.lineWidth = SCRATCH_RADIUS * 2
  ctx.lineCap = "round"
  ctx.beginPath()
  ctx.moveTo(from.x, from.y)
  ctx.lineTo(to.x + 0.01, to.y)
  ctx.stroke()
}

/** Grid cells (of {@link SCRATCH_COLS} x {@link SCRATCH_ROWS}) a coin stroke from `from` to `to` uncovers on a foil of `width` x `height`. */
export function scrapedCells(
  from: { x: number; y: number },
  to: { x: number; y: number },
  width: number,
  height: number,
): number[] {
  const cells = new Set<number>()
  const steps = Math.max(1, Math.ceil(Math.hypot(to.x - from.x, to.y - from.y) / (SCRATCH_RADIUS / 2)))
  for (let step = 0; step <= steps; step += 1) {
    const x = from.x + ((to.x - from.x) * step) / steps
    const y = from.y + ((to.y - from.y) * step) / steps
    for (let cell = 0; cell < SCRATCH_COLS * SCRATCH_ROWS; cell += 1) {
      const cx = ((cell % SCRATCH_COLS) + 0.5) * (width / SCRATCH_COLS)
      const cy = (Math.floor(cell / SCRATCH_COLS) + 0.5) * (height / SCRATCH_ROWS)
      if (Math.hypot(cx - x, cy - y) <= SCRATCH_RADIUS) cells.add(cell)
    }
  }
  return [...cells]
}

const MAX_FLAKES = 24

/** Drops a few silver flakes from `at` inside `layer` that fall, turn and fade out, keeping at most a couple dozen alive. */
export function dropFlakes(layer: HTMLElement, at: { x: number; y: number }) {
  if (typeof layer.animate !== "function" || layer.childElementCount >= MAX_FLAKES) return
  for (let index = 0; index < 2; index += 1) {
    const flake = document.createElement("span")
    const size = 3 + Math.random() * 4
    flake.style.cssText = `position:absolute;left:${at.x}px;top:${at.y}px;width:${size}px;height:${size * 0.7}px;border-radius:1px;background:${Math.random() > 0.5 ? foil.light : foil.deep};box-shadow:0 1px 1px ${gameRgba("ink", 0.2)};pointer-events:none`
    layer.appendChild(flake)
    const drift = (Math.random() - 0.5) * 40
    const fall = 30 + Math.random() * 40
    const turn = (Math.random() - 0.5) * 540
    flake
      .animate(
        [
          { transform: "translate(0, 0) rotate(0deg)", opacity: 1 },
          { transform: `translate(${drift}px, ${fall}px) rotate(${turn}deg)`, opacity: 0 },
        ],
        { duration: toMs(GAME_DURATION.flake + Math.random() * GAME_DURATION.flakeSpread), easing: cssEase(GAME_EASE.flake) },
      )
      .finished.then(
        () => flake.remove(),
        () => flake.remove(),
      )
  }
}

/** How long the "Raspar o cartão" auto-scratch takes, in milliseconds. */
export const AUTO_SCRATCH_MS = toMs(GAME_DURATION.autoScratch)

/** The auto-scratch zigzag over a foil of `width` x `height`: four sweeps from side to side, top to bottom. */
export function autoScratchPath(width: number, height: number): { x: number; y: number }[] {
  return [0.16, 0.34, 0.52, 0.7, 0.88].map((row, index) => ({
    x: (index % 2 === 0 ? 0.08 : 0.92) * width,
    y: row * height,
  }))
}

/** The point at share `t` (0 to 1) of the way along `path`. */
export function pointAlong(path: readonly { x: number; y: number }[], t: number): { x: number; y: number } {
  const first = path[0] ?? { x: 0, y: 0 }
  const lengths = path.slice(1).map((point, index) => {
    const from = path[index] ?? point
    return Math.hypot(point.x - from.x, point.y - from.y)
  })
  let left = Math.min(1, Math.max(0, t)) * lengths.reduce((sum, length) => sum + length, 0)
  for (let index = 0; index < lengths.length; index += 1) {
    const length = lengths[index] ?? 0
    const from = path[index] ?? first
    const to = path[index + 1] ?? from
    if (left <= length || index === lengths.length - 1) {
      const share = length === 0 ? 1 : Math.min(1, left / length)
      return { x: from.x + (to.x - from.x) * share, y: from.y + (to.y - from.y) * share }
    }
    left -= length
  }
  return first
}

/** Kind of prize a label names, which picks its art: percent off, free delivery, a free item or a fixed amount off. */
export type PrizeKind = "percent" | "delivery" | "item" | "amount"

/** Reads the kind of prize from its label (the storefront gets no prize type): "%" is percent, "frete"/"entrega" is delivery, "R$"/"reais"/"desconto"/"off" is an amount, anything else a free item. */
export function prizeKindOf(label: string): PrizeKind {
  const text = label
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
  if (/%|por ?cento/.test(text)) return "percent"
  if (/frete|entrega|delivery/.test(text)) return "delivery"
  if (/r\$|\breais\b|desconto|\boff\b|cashback/.test(text)) return "amount"
  return "item"
}

